/**
 * The markup a compose body is seeded with: where the caret lands, and how the
 * signature and the quoted message are spaced.
 *
 * 2026-09-26 — Claude Code (Fable 5.1), at Bob's direction.
 * Spacing used to be spacer ELEMENTS, added independently in three places:
 * quoteBody/forwardBody led with `<p></p><br><br>`, the signature block with
 * `<br>`, and the reply glue put another `<br>` on each side of the signature.
 * TinyMCE wraps every bare top-level `<br>` in a `<p>` and pads each empty
 * `<p>` with `&nbsp;`, so a reply went out with four blank paragraphs between
 * the signature and the quote (Bob: "why the <br> and do you really need
 * &nbsp;").
 *
 * Now each block carries its own gap as an inline `margin-top`, and there is
 * exactly one empty paragraph, for the caret.
 *   - Inline, because the sent letter is a bare fragment with no `<style>`;
 *     an inline style is also the only form that survives a recipient's
 *     reply or forward.
 *   - Margin, not padding: padding would stretch div.reply's left border bar
 *     up into the gap.
 * The class names are markers, not styling hooks in the sent letter:
 * `mailx-sig` and `reply` are what splitWordEditable() cuts at, and `reply`
 * is what the editor's own stylesheet draws the quote bar on.
 */

/** The one empty paragraph the caret lands in. `<p><br></p>` rather than
 *  `<p></p>`: it is the form both TinyMCE and Quill use for an empty line. */
export const CARET_PARAGRAPH = "<p><br></p>";

/** Gap above the signature and above the quoted message. */
export const BLOCK_GAP_STYLE = "margin-top:1em";

/** The account signature, wrapped in its marker div. */
export function signatureBlock(sigHtml: string) {
    return `<div class="mailx-sig" style="${BLOCK_GAP_STYLE}">${sigHtml}</div>`;
}

/** The quoted or forwarded message, wrapped in its marker div. */
export function replyBlock(innerHtml: string) {
    return `<div class="reply" style="${BLOCK_GAP_STYLE}">${innerHtml}</div>`;
}

/**
 * Assemble the body a new compose opens with.
 * @param body - what the opener supplied: a replyBlock() for reply/forward, a mailto/share body, or empty
 * @param sigHtml - the account signature as HTML, or empty when there is none
 * @param isReplyForward - the body is a quoted message, so the user's text and the signature go ABOVE it
 */
export function seedBody(body: string, sigHtml: string, isReplyForward: boolean) {
    const sig = sigHtml ? signatureBlock(sigHtml) : "";
    if (isReplyForward)
        return `${CARET_PARAGRAPH}${sig}${body}`;
    if (!sig)
        return body;
    // New mail: the signature goes at the end. An empty body still needs a
    // paragraph above the signature, or the only place to type is inside it.
    return `${body || CARET_PARAGRAPH}${sig}`;
}
